import { combineReducers, configureStore, Reducer, StoreEnhancer, Tuple } from '@reduxjs/toolkit';
import { createInjectorsEnhancer } from "redux-injectors";
import createSagaMiddleware from 'redux-saga';

import accountReducer from '@/entities/ContentEntities/AccountAdjustment/AccountMasterManagement/model/slice';
import tradeTransactionReducer from '@/entities/ContentEntities/AccountAdjustment/AutoReceiptVoucherManage/model/purchaseCardSlice';
import cardDetailReducer from '@/entities/ContentEntities/AccountAdjustment/ClientRegister/model/cardDetailSlice';
import bindingMiddleware from '@/shared/ui/XadomTable/model/middleware/bindingMiddleware';

import { staticReducers } from './rootReducer';

/**
 * Redux Store 설정 및 생성을 담당하는 모듈
 * 
 * @module appStore
 */

/**
 * Redux-Saga 미들웨어 인스턴스를 생성합니다.
 * 비동기 액션 처리를 위한 미들웨어입니다.
 */
const sagaMiddleware = createSagaMiddleware();
const runSaga = sagaMiddleware.run;

/**
 * 정적 리듀서와 동적으로 주입된 리듀서를 결합하여 루트 리듀서를 생성합니다.
 * 
 * @param injectedReducers - 동적으로 주입될 리듀서 객체
 * @returns 결합된 루트 리듀서
 */
function createReducer(injectedReducers: { [key: string]: Reducer } = {}) {
    return combineReducers({
        ...staticReducers,
        ...injectedReducers,
        cardDetail: cardDetailReducer,
        account: accountReducer,
        tradeTransaction: tradeTransactionReducer,
    });
}

/** Store 확장자 타입 정의 */
type Enhancers = ReadonlyArray<StoreEnhancer>;

/**
 * Redux Store의 기능을 확장하는 enhancer들을 생성합니다.
 * 동적 리듀서 주입을 위한 enhancer가 포함됩니다.
 * 
 * @returns Store enhancer 배열
 */
function getEnhancers() {
    const enhancers = [
        createInjectorsEnhancer({
            createReducer,
            runSaga,
        }),
    ];
    return enhancers as Tuple<Enhancers>;
}

/**
 * Redux Store를 구성하고 생성합니다.
 * - 동적 리듀서 주입 지원
 * - Redux-Saga 미들웨어 통합
 * - 커스텀 바인딩 미들웨어 적용
 * 
 * @returns 구성된 Redux Store 인스턴스
 */
function makeStore() {
    return configureStore({
        reducer: createReducer(),
        middleware: (getDefaultMiddleware) =>
            getDefaultMiddleware({
                serializableCheck: false,
                immutableCheck: false,
            })
                .prepend(bindingMiddleware.middleware)
                .concat(sagaMiddleware),
        enhancers: (getDefaultEnhancers) => 
            getDefaultEnhancers().concat(getEnhancers()),
    });
}

/** 애플리케이션의 Redux Store 인스턴스 */
export const appStore = makeStore();

/** Store의 상태 타입 */
export type RootState = ReturnType<typeof appStore.getState>;

/** Store의 dispatch 함수 타입 */
export type AppDispatch = typeof appStore.dispatch;

export default appStore;